import { ErrorModal, getMomentTz } from "@dexteel/mesf-core";
import { Delete } from "@mui/icons-material";
import { IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { Autocomplete } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { CasterPourbackReason } from "../../../../models/Catalogs";
import {
	deletePourback,
	upsertPourback,
} from "../../../../repositories/ProductionRepository";
import { useDeliveryTicketStyles } from "./styles/deliveryTicketStyles";

const moment = getMomentTz();

interface FormData {
	pourbackWeight: number | null;
	selectedReason: CasterPourbackReason | null;
}

interface Props {
	processId: number;
	heatId: number;
	pourbackId: number | null;
	tapTime: string | null;
	initialPourbackWeight: number | null;
	initialPourbackReasonId: number | null;
	pourbackReasons: CasterPourbackReason[];
	onSaveSuccess: () => Promise<void>;
	onPourbackChange: (weight: number | null, reasonId: number | null) => void;
}

export const PourbackSection = ({
	processId,
	heatId,
	pourbackId,
	tapTime,
	initialPourbackWeight,
	initialPourbackReasonId,
	pourbackReasons,
	onSaveSuccess,
	onPourbackChange,
}: Props) => {
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const classes = useDeliveryTicketStyles();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormData>({
		mode: "onChange",
		defaultValues: {
			pourbackWeight: null,
			selectedReason: null,
		},
	});

	// Watch form fields to notify parent of changes
	const watchedWeight = useWatch({ control, name: "pourbackWeight" });
	const watchedReason = useWatch({ control, name: "selectedReason" });

	// Reset form when initial values change
	useEffect(() => {
		const reason = pourbackReasons.find(
			(r) => r.PourbackReasonId === initialPourbackReasonId,
		);
		reset({
			pourbackWeight: initialPourbackWeight,
			selectedReason: reason || null,
		});
	}, [initialPourbackWeight, initialPourbackReasonId, pourbackReasons, reset]);

	// Notify parent when form values change
	useEffect(() => {
		onPourbackChange(watchedWeight, watchedReason?.PourbackReasonId || null);
	}, [watchedWeight, watchedReason, onPourbackChange]);

	const onSubmit = async (data: FormData) => {
		setIsLoading(true);
		setError("");

		if (data.pourbackWeight !== null && data.selectedReason) {
			const sourceTime = tapTime
				? moment(tapTime).toDate()
				: moment.utc().toDate();

			const resp = await upsertPourback(
				pourbackId,
				heatId,
				processId,
				data.pourbackWeight,
				data.selectedReason.PourbackReasonId,
				sourceTime,
			);

			if (resp.ok) {
				await onSaveSuccess();
			} else {
				setError(resp.message);
			}
		} else {
			setError("Please fill all the fields");
		}

		setIsLoading(false);
	};

	const handleClear = async () => {
		setIsLoading(true);
		setError("");

		// If there's an existing pourback in the database, delete it
		if (pourbackId) {
			const resp = await deletePourback(pourbackId);
			if (resp.ok) {
				await onSaveSuccess();
			} else {
				setError(resp.message);
				setIsLoading(false);
				return;
			}
		}

		// Reset form fields
		reset({
			pourbackWeight: null,
			selectedReason: null,
		});

		setIsLoading(false);
	};

	return (
		<>
			{/* Subtitle */}
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				size={{ xs: 12, md: 12 }}
			>
				<Typography variant="h6" style={{ fontWeight: "bold" }}>
					Pourback Declaration
				</Typography>
			</Grid>

			{/* Pourback Weight */}
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				spacing={2}
				size={{ xs: 12, md: 12 }}
			>
				<Grid size={{ xs: 12, md: 3 }}>
					<Controller
						name="pourbackWeight"
						control={control}
						rules={{
							min: { value: 0, message: "Minimum Weight is 0" },
							max: { value: 200, message: "Maximum Weight is 200" },
						}}
						render={({ field }) => (
							<TextField
								{...field}
								type="number"
								label="Pourback Weight [tons]"
								variant="outlined"
								size="small"
								fullWidth
								error={!!errors.pourbackWeight}
								helperText={errors.pourbackWeight?.message}
								onFocus={(e) => e.target.select()}
								inputProps={{ min: 0, max: 200, step: 0.01 }}
								className={classes.input}
							/>
						)}
					/>
				</Grid>

				{/* Pourback Reason */}
				<Grid size={{ xs: 12, md: 3 }}>
					<Controller
						name="selectedReason"
						control={control}
						render={({ field }) => (
							<Autocomplete<CasterPourbackReason>
								{...field}
								options={pourbackReasons}
								getOptionLabel={(reason: CasterPourbackReason) =>
									reason.PourbackReasonName
								}
								renderInput={(params: any) => (
									<TextField
										{...params}
										label="Pourback Reason"
										variant="outlined"
										size="small"
										className={classes.input}
									/>
								)}
								onChange={(_, value) => field.onChange(value)}
								openOnFocus
								autoHighlight
								fullWidth
								size="small"
							/>
						)}
					/>
				</Grid>

				{/* Delete Button */}
				<Grid
					size={{ xs: 6, md: 1 }}
					container
					alignItems="center"
					justifyContent="center"
				>
					<Tooltip title="Delete pourback declaration">
						<IconButton
							onClick={handleClear}
							disabled={isLoading}
							color="error"
							size="medium"
						>
							<Delete />
						</IconButton>
					</Tooltip>
				</Grid>
			</Grid>

			{/* Error Modal */}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
